import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { contact, cookieConsent, legalLinks } from '../data/contact';
type Consent = {
  necessary: true;
  measurement: boolean;
  decidedAt: string;
};
export const OPEN_COOKIE_SETTINGS = 'sl:open-cookie-settings';
const ease = [0.22, 1, 0.36, 1] as const;
export function CookieBanner() {
  const reduceMotion = useReducedMotion();
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [measurement, setMeasurement] = useState(false);
  useEffect(() => {
    let timer: number | undefined;
    try {
      const stored = window.localStorage.getItem(cookieConsent.storageKey);
      if (stored) setMeasurement((JSON.parse(stored) as Consent).measurement);else timer = window.setTimeout(() => setVisible(true), 1200);
    } catch {
      setVisible(true);
    }
    const open = () => {
      setCustomizing(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_COOKIE_SETTINGS, open);
    return () => {
      window.removeEventListener(OPEN_COOKIE_SETTINGS, open);
      if (timer) window.clearTimeout(timer);
    };
  }, []);
  const save = (allowMeasurement: boolean) => {
    const consent: Consent = {
      necessary: true,
      measurement: allowMeasurement,
      decidedAt: new Date().toISOString()
    };
    try {
      window.localStorage.setItem(cookieConsent.storageKey, JSON.stringify(consent));
    } catch {
      /* storage unavailable — consent applies to this visit only */
    }
    setMeasurement(allowMeasurement);
    setVisible(false);
    setCustomizing(false);
  };
  const link = 'underline underline-offset-2 transition-opacity duration-150 hover:opacity-60';
  const btn = 'h-10 px-5 text-[14px] font-medium transition-opacity duration-150 hover:opacity-80';
  return <AnimatePresence data-id="element-274" data-mp="15jwuk95ejma">
      {visible && <motion.div role="dialog" aria-modal="false" aria-labelledby="cookie-title" className="fixed bottom-4 left-4 right-4 z-[60] border border-line bg-paper text-ink shadow-[0_12px_40px_rgba(0,0,0,0.12)] sm:bottom-6 sm:left-6 sm:right-auto sm:w-[400px]" initial={{
      opacity: 0,
      y: reduceMotion ? 0 : 12
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: reduceMotion ? 0 : 12
    }} transition={{
      duration: 0.3,
      ease
    }} data-id="element-275" data-mp="1379tsoj9j2d">
          <div className="p-6" data-id="element-276" data-mp="2piz2mzdmxixy">
            <h2 id="cookie-title" className="font-editorial text-[22px] leading-[28px]" data-id="element-277" data-mp="2pipm02ehco4x">
              A note on privacy
            </h2>
            <p className="mt-3 text-[13px] font-light leading-[20px] text-ink/75" data-id="element-278" data-mp="2lhnn5moh6e3h">
              We ({contact.domain}) and selected third parties ({cookieConsent.thirdPartyCount}) use cookies for technical
              purposes and, with your consent, for measurement — as set out in our{' '}
              <Link to={legalLinks.privacyPolicy} className={link} data-id="element-279" data-mp="erh73bup0raf">
                privacy policy
              </Link>{' '}
              and{' '}
              <Link to={legalLinks.cookiePolicy} className={link} data-id="element-280" data-mp="r99xqs6uldli">
                cookie policy
              </Link>
              . You may change your choice at any time.
            </p>

            <AnimatePresence initial={false} data-id="element-281" data-mp="10zd03izv03wi">
              {customizing && <motion.fieldset initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.25,
            ease
          }} className="overflow-hidden" data-id="element-282" data-mp="111604ffyvpvj">
                  <legend className="sr-only" data-id="element-283" data-mp="hb404ms2qtmg">Cookie categories</legend>
                  <div className="mt-4 divide-y divide-line border-y border-line" data-id="element-284" data-mp="h99ch0pf4neo">
                    <div className="flex items-center justify-between gap-4 py-3" data-id="element-285" data-mp="1ooe7r40vbb0q">
                      <span className="text-[13px]" data-id="element-286" data-mp="1sp7s5nf3q7ov">Strictly necessary</span>
                      <span className="text-[12px] font-light text-ink/60" data-id="element-287" data-mp="1r8k871q9wjcj">Always on</span>
                    </div>
                    <label className="flex cursor-pointer items-center justify-between gap-4 py-3" data-id="element-288" data-mp="1mbbx1l3jlfks">
                      <span className="text-[13px]" data-id="element-289" data-mp="218b3h5mnr2do">Measurement</span>
                      <span className="relative inline-flex" data-id="element-290" data-mp="1w7x6cimlhcuf">
                        <input type="checkbox" checked={measurement} onChange={e => setMeasurement(e.target.checked)} className="peer sr-only" data-id="element-291" data-mp="1wajeokhb7wdp" />
                        <span className="h-5 w-9 border border-ink/40 bg-paper transition-colors duration-200 peer-checked:border-ink peer-checked:bg-ink peer-focus-visible:outline peer-focus-visible:outline-1 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-ink" data-id="element-292" data-mp="2jxhpp2mnuc48" />
                        <span className="pointer-events-none absolute left-[3px] top-[3px] h-3.5 w-3.5 bg-ink transition-[transform,background-color] duration-200 ease-luxe peer-checked:translate-x-4 peer-checked:bg-paper" data-id="element-293" data-mp="2oy5fxm32eoxe" />
                      </span>
                    </label>
                  </div>
                </motion.fieldset>}
            </AnimatePresence>

            <div className="mt-5 flex items-center gap-2" data-id="element-294" data-mp="33jb6kidzptc1">
              <button type="button" onClick={() => save(customizing ? measurement : true)} className={`${btn} flex-1 bg-ink text-paper`} data-id="element-295" data-mp="33lxcyep5a2so">
                {customizing ? 'Save' : 'Accept'}
              </button>
              <button type="button" onClick={() => save(false)} className={`${btn} flex-1 border border-ink`} data-id="element-296" data-mp="2tnhpclpbtbnx">
                Reject
              </button>
            </div>
            {!customizing && <button type="button" onClick={() => setCustomizing(true)} className="mt-3 text-[13px] font-light text-ink/70 transition-colors duration-150 hover:text-ink" data-id="element-297" data-mp="25w2eu5nebo1l">
                Customise preferences
              </button>}
          </div>
        </motion.div>}
    </AnimatePresence>;
}